// Slide-out chat: your conversations, a thread view with composer, and a "new message / group" picker.
// Direct messages and group chats share one list; unread counts come from the threads query. A thread is
// formatted messages with photos and files (ChatMessages), the rich composer (ChatComposer) and a lightbox
// for the photos; files can also be dropped anywhere on the conversation.
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type DragEvent as ReactDragEvent } from 'react';
import { chatTextToHtml, type ChatReactionEmoji } from '@technohull/shared';
import { Avatar } from '../components/Avatar.js';
import { InlineError, InlineLoading, useConfirm } from '../components/ui.js';
import { IconBack, IconCheck, IconClose, IconCompose, IconPaperclip, IconUsers } from '../components/icons.js';
import { useCollab } from './CollabContext.js';
import { useBackToClose } from '../lib/media.js';
import { useDateFormatter } from '../lib/format.js';
import { localTodayIso, type DateFormatter } from '../lib/dates.js';
import { fold, hasText } from '../lib/search.js';
import {
  useCreateThread,
  useDeleteMessage,
  useEditMessage,
  useMarkRead,
  usePeople,
  useReact,
  useSendMessage,
  useThreadMessages,
  useThreads,
  type CollabMessage,
  type CollabThread,
} from './collabHooks.js';
import { ChatComposer } from './ChatComposer.js';
import { ChatMessages } from './ChatMessages.js';
import { ChatLightbox } from './ChatLightbox.js';
import { filesOf, useChatUploads } from './chatUploads.js';
import { threadPhotos } from './chatFormat.js';

// today's messages show the time in the thread list; older ones the date
const isToday = (ms: number) => localTodayIso(new Date(ms)) === localTodayIso();
const chatWhen = (df: DateFormatter, ms: number) => (isToday(ms) ? df.time(ms) : df.date(ms, { year: 'auto' }));
const threadName = (t: CollabThread) => t.title || t.members.map((m) => m.name).join(', ') || 'Conversation';

export function ChatPanel() {
  const { chatOpen, closeChat, activeThread, setActiveThread, dmError, clearDmError } = useCollab();
  const [mode, setMode] = useState<'list' | 'new'>('list');
  const threads = useThreads(chatOpen);
  useBackToClose(chatOpen, closeChat); // touch: the back gesture closes the panel, not the page
  // a failed "Message" from the presence menu reports in the list view, so make sure that's the one showing.
  useEffect(() => { if (dmError) setMode('list'); }, [dmError]);

  // the panel renders before the topbar in the DOM, so move focus into it on open (and back on close);
  // Escape closes it like the other overlays. What had the focus is read as the panel opens, before a
  // conversation's message box takes the focus (and then the panel leaves it there).
  const panelRef = useRef<HTMLElement>(null);
  const [wasOpen, setWasOpen] = useState(chatOpen);
  const [opener, setOpener] = useState<HTMLElement | null>(null);
  if (chatOpen !== wasOpen) {
    setWasOpen(chatOpen);
    if (chatOpen) setOpener(document.activeElement as HTMLElement | null);
  }
  useEffect(() => {
    if (!chatOpen) return;
    if (!panelRef.current?.contains(document.activeElement)) panelRef.current?.focus();
    // (a popup inside the panel that handles Escape itself marks it handled)
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) closeChat(); };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); if (opener?.isConnected) opener.focus?.(); };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [chatOpen]);

  if (!chatOpen) return null;
  const active = threads.data?.threads.find((t) => t.id === activeThread) ?? null;

  return (
    <>
      <div className="chat-scrim" onClick={closeChat} />
      <aside ref={panelRef} tabIndex={-1} className="chat-panel" role="dialog" aria-label="Messages">
        <div className="chat-hd">
          {activeThread ? (
            <button className="chat-icon" onClick={() => setActiveThread(null)} aria-label="Back" title="Back"><IconBack size={17} /></button>
          ) : mode === 'new' ? (
            <button className="chat-icon" onClick={() => setMode('list')} aria-label="Back" title="Back"><IconBack size={17} /></button>
          ) : (
            <button className="chat-icon" onClick={() => setMode('new')} aria-label="New message" title="New message"><IconCompose size={17} /></button>
          )}
          <b className="chat-title">{activeThread ? (active ? threadName(active) : 'Conversation') : mode === 'new' ? 'New message' : 'Messages'}</b>
          <button className="chat-icon" onClick={closeChat} aria-label="Close" title="Close"><IconClose size={17} /></button>
        </div>

        {activeThread ? (
          <ThreadView key={activeThread} threadId={activeThread} />
        ) : mode === 'new' ? (
          <NewChat onStarted={(id) => { setActiveThread(id); setMode('list'); }} />
        ) : (
          <ThreadList
            dmError={dmError}
            onDismissDmError={clearDmError}
            threads={threads.data?.threads ?? []}
            loading={threads.isLoading}
            error={threads.isError && !threads.data ? threads.error : null}
            onOpen={(id) => setActiveThread(id)}
            onNew={() => setMode('new')}
          />
        )}
      </aside>
    </>
  );
}

function ThreadList({ threads, loading, error, dmError, onDismissDmError, onOpen, onNew }: {
  threads: CollabThread[]; loading: boolean; error: unknown; dmError: Error | null; onDismissDmError: () => void; onOpen: (id: string) => void; onNew: () => void;
}) {
  const df = useDateFormatter();
  return (
    <div className="chat-list">
      {dmError && <div style={{ padding: '0 10px' }}><InlineError error={dmError} onDismiss={onDismissDmError} /></div>}
      {loading && <InlineLoading />}
      {error ? (
        <div className="chat-empty"><InlineError error={error} /></div>
      ) : (
        !loading && threads.length === 0 && (
          <div className="chat-empty">
            <p className="muted">No conversations yet.</p>
            <button className="btn btn-sm btn-primary" onClick={onNew}>Start a message</button>
          </div>
        )
      )}
      {threads.map((t) => (
        <button key={t.id} className="chat-row" onClick={() => onOpen(t.id)}>
          {t.isGroup ? <span className="chat-group-av"><IconUsers size={18} /></span> : <Avatar name={t.members[0]?.name ?? threadName(t)} avatar={t.members[0]?.avatar} size={34} />}
          <span className="chat-row-main">
            <span className="chat-row-top"><b>{threadName(t)}</b>{t.lastMessage && <span className="chat-row-time">{chatWhen(df, t.lastMessage.at)}</span>}</span>
            <span className="chat-row-sub muted">{t.lastMessage ? `${t.lastMessage.fromName.split(' ')[0]}: ${t.lastMessage.body}` : 'No messages yet'}</span>
          </span>
          {t.unread > 0 && <span className="chat-unread">{t.unread}</span>}
        </button>
      ))}
    </div>
  );
}

function ThreadView({ threadId }: { threadId: string }) {
  const messages = useThreadMessages(threadId);
  const send = useSendMessage();
  const edit = useEditMessage();
  const del = useDeleteMessage();
  const react = useReact();
  const markRead = useMarkRead();
  const uploads = useChatUploads(threadId);
  const [confirmNode, confirm] = useConfirm();
  const [editing, setEditing] = useState<CollabMessage | null>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const [unseen, setUnseen] = useState(0);
  const scroller = useRef<HTMLDivElement>(null);
  const composerFocus = useRef<(() => void) | null>(null);
  const focusComposer = () => composerFocus.current?.();
  const atBottom = useRef(true);
  const lastSeen = useRef(0);
  const dragDepth = useRef(0);
  const list = useMemo(() => messages.data?.messages ?? [], [messages.data]);
  const photos = useMemo(() => threadPhotos(list), [list]);
  // failed with nothing cached: a failed background poll keeps the thread we already have on screen.
  const loadFailed = messages.isError && !messages.data;
  const lastId = list.length ? list[list.length - 1]!.id : 0;
  const actionError = send.error ?? edit.error ?? del.error ?? react.error;

  const toBottom = () => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  };
  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (atBottom.current) setUnseen(0);
  };

  // new messages: follow them down when already at the bottom (or when they're mine), else count them
  useLayoutEffect(() => {
    if (!lastId) return;
    const before = lastSeen.current;
    lastSeen.current = lastId;
    if (!before || atBottom.current || list[list.length - 1]?.mine) {
      toBottom();
      atBottom.current = true;
      setUnseen(0);
    } else {
      setUnseen((u) => u + list.filter((m) => m.id > before && !m.mine).length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastId]);
  // the composer growing (more lines, files waiting) or the panel resizing keeps the newest in view
  useEffect(() => {
    const el = scroller.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => { if (atBottom.current) toBottom(); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // mark the thread read whenever new messages land.
  useEffect(() => { if (lastId) markRead.mutate({ thread: threadId, lastId }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [threadId, lastId]);

  const onSend = async (html: string) => {
    try {
      // the files ready now go with it; one added while it sends stays for the next message
      const sent = uploads.readyIds;
      await send.mutateAsync({ thread: threadId, html, attachments: sent });
      uploads.reset(sent);
      return true;
    } catch {
      return false;
    }
  };
  const onSave = async (id: number, html: string) => {
    try {
      await edit.mutateAsync({ thread: threadId, id, html });
      setEditing(null);
      return true;
    } catch {
      return false;
    }
  };
  const startEdit = (m: CollabMessage) => {
    if (!m.mine || m.deleted) return;
    edit.reset();
    setEditing(m);
  };
  const editLast = () => {
    const m = [...list].reverse().find((x) => x.mine && !x.deleted);
    if (m) startEdit(m);
  };
  const remove = async (m: CollabMessage) => {
    const ok = await confirm({
      title: 'Delete message',
      message: m.attachments.length
        ? 'Delete this message and its files for everyone in the conversation? This can’t be undone.'
        : 'Delete this message for everyone in the conversation? This can’t be undone.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    if (editing?.id === m.id) setEditing(null);
    del.mutate({ thread: threadId, id: m.id });
    // its Delete button goes with it: the focus goes to the message box, once the dialog has handed it back
    setTimeout(focusComposer, 0);
  };
  const onReact = (m: CollabMessage, emoji: ChatReactionEmoji, on: boolean) => react.mutate({ thread: threadId, id: m.id, emoji, on });
  const openPhoto = (id: string) => {
    const i = photos.findIndex((p) => p.id === id);
    if (i >= 0) setViewing(i);
  };

  // files dropped anywhere on the conversation go with the next message
  const hasFiles = (e: ReactDragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
  const dnd = {
    onDragEnter: (e: ReactDragEvent) => { if (!hasFiles(e)) return; e.preventDefault(); dragDepth.current += 1; setDragging(true); },
    onDragOver: (e: ReactDragEvent) => { if (!hasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = editing ? 'none' : 'copy'; },
    onDragLeave: (e: ReactDragEvent) => {
      if (!hasFiles(e)) return;
      dragDepth.current -= 1;
      if (dragDepth.current <= 0) { dragDepth.current = 0; setDragging(false); }
    },
    onDrop: (e: ReactDragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      if (!editing) uploads.add(filesOf(e.dataTransfer));
    },
  };

  return (
    <div className="chat-thread" {...dnd}>
      <div className="chat-msgs" ref={scroller} onScroll={onScroll}>
        {messages.isLoading && <InlineLoading />}
        <ChatMessages
          messages={list}
          editingId={editing?.id ?? null}
          onReact={onReact}
          onEdit={startEdit}
          onDelete={(m) => void remove(m)}
          onOpenPhoto={openPhoto}
          onMediaLoad={() => { if (atBottom.current) toBottom(); }}
          focusComposer={focusComposer}
        />
        {loadFailed && <div className="chat-empty"><InlineError error={messages.error} /></div>}
        {!messages.isLoading && !loadFailed && list.length === 0 && <div className="muted chat-empty">No messages yet - say hello.</div>}
        {unseen > 0 && (
          <button type="button" className="chat-jump" onClick={() => { toBottom(); setUnseen(0); }}>
            {unseen === 1 ? '1 new message' : `${unseen} new messages`} ↓
          </button>
        )}
      </div>
      {actionError && (
        <div className="chat-err">
          <InlineError error={actionError} onDismiss={() => { send.reset(); edit.reset(); del.reset(); react.reset(); }} />
        </div>
      )}
      <ChatComposer
        thread={threadId}
        uploads={uploads}
        editing={editing ? { id: editing.id, html: editing.html ?? chatTextToHtml(editing.body), hasFiles: editing.attachments.length > 0 } : null}
        onCancelEdit={() => setEditing(null)}
        onSend={onSend}
        onSave={onSave}
        onEditLast={editLast}
        sending={send.isPending || edit.isPending}
        focusRef={composerFocus}
      />
      {dragging && (
        <div className="chat-drop" aria-hidden="true">
          <IconPaperclip size={22} />
          <span>{editing ? 'Finish editing to add files' : 'Drop files to send them'}</span>
        </div>
      )}
      {viewing != null && photos.length > 0 && (
        <ChatLightbox photos={photos} index={viewing} onIndex={setViewing} onClose={() => setViewing(null)} />
      )}
      {confirmNode}
    </div>
  );
}

function NewChat({ onStarted }: { onStarted: (id: string) => void }) {
  const people = usePeople(true);
  const create = useCreateThread();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [title, setTitle] = useState('');
  const [q, setQ] = useState('');
  const list = useMemo(
    () => (people.data?.people ?? []).filter((p) => hasText(fold(q.trim()), p.name)),
    [people.data, q],
  );
  const toggle = (uid: string) => setPicked((s) => { const n = new Set(s); n.has(uid) ? n.delete(uid) : n.add(uid); return n; });
  const start = () => {
    if (!picked.size || create.isPending) return;
    create.mutate(
      { memberUids: [...picked], title: picked.size > 1 && title.trim() ? title.trim() : undefined },
      { onSuccess: (r) => onStarted(r.id) },
    );
  };

  return (
    <div className="chat-new">
      <input className="field" placeholder="Search people…" aria-label="Search people" value={q} onChange={(e) => setQ(e.target.value)} />
      {picked.size > 1 && <input className="field" placeholder="Group name (optional)" aria-label="Group name" value={title} onChange={(e) => setTitle(e.target.value)} />}
      <div className="chat-people">
        {people.isLoading && <InlineLoading />}
        {list.map((p) => (
          <button key={p.uid} className={`chat-person ${picked.has(p.uid) ? 'on' : ''}`} aria-pressed={picked.has(p.uid)} onClick={() => toggle(p.uid)}>
            <Avatar name={p.name} avatar={p.avatar} size={30} />
            <span>{p.name}</span>
            {picked.has(p.uid) && <span className="chat-person-check"><IconCheck size={13} /></span>}
          </button>
        ))}
        {people.isError && !people.data ? (
          <div className="chat-empty"><InlineError error={people.error} /></div>
        ) : (
          !people.isLoading && list.length === 0 && (
            <div className="muted chat-empty">{q.trim() ? `No one matches “${q.trim()}”.` : 'No one to message yet.'}</div>
          )
        )}
      </div>
      {create.isError && <InlineError error={create.error} onDismiss={() => create.reset()} />}
      <button className="btn btn-sm btn-primary chat-start" onClick={start} disabled={!picked.size || create.isPending}>
        {picked.size > 1 ? `Start group (${picked.size})` : 'Start chat'}
      </button>
    </div>
  );
}
